import React, { Suspense, useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Redirect, useLocation } from "react-router-dom";
import LoginPage from "../pages/LoginPage";
import Navbar from "./Navbar";
import { useAccount, useSignMessage, useDisconnect } from "wagmi";
import { areEqual, getEncryptedAuthToken, trimAddress } from "../pureFunctions";
import {
  AuthenticatedUser,
  canAccessAdminPathname,
  clearStoredAuthSession,
  createEmailAuthSession,
  createWalletAuthSession,
  getDefaultAdminPath,
  getAuthDisplayName,
  getStoredAuthSession,
  isAllowedAdminUserType,
  setStoredAuthSession,
} from "../auth/session";
import { signIn, signInWithEmail } from "../gql/gql";
import { AuthenticatedUserContext } from "../auth/AuthenticatedUserContext";

type Props = {
  children: React.ReactNode;
  // Navbar: React.ReactNode;
};

const Layout = (props: Props) => {
  const { children } = props;
  const classes = useStyles();
  const { pathname } = useLocation();

  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(
    null,
  );
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isWalletAuthDialogOpen, setIsWalletAuthDialogOpen] = useState(false);
  const [isWalletAuthSubmitting, setIsWalletAuthSubmitting] = useState(false);
  const [walletAuthError, setWalletAuthError] = useState<string | null>(null);

  const { address, isConnected, isConnecting, isReconnecting } = useAccount();

  const { signMessageAsync } = useSignMessage();

  const { disconnect } = useDisconnect();

  useEffect(() => {
    let isMounted = true;
    const storedSession = getStoredAuthSession();

    const resetAuthState = () => {
      if (isMounted) {
        setAuthenticatedUser(null);
      }
    };

    const finishAuthCheck = () => {
      if (isMounted) {
        setIsAuthChecking(false);
      }
    };

    const openWalletAuthDialog = () => {
      if (isMounted) {
        setWalletAuthError(null);
        setIsWalletAuthSubmitting(false);
        setIsWalletAuthDialogOpen(true);
      }
    };

    const closeWalletAuthDialog = () => {
      if (isMounted) {
        setWalletAuthError(null);
        setIsWalletAuthSubmitting(false);
        setIsWalletAuthDialogOpen(false);
      }
    };

    const restoreWalletSession = async (walletAddress: string, signature: string) => {
      const token = getEncryptedAuthToken(signature);

      try {
        const user = await signIn(walletAddress, token);
        if (!user || !isAllowedAdminUserType(user.userType)) {
          clearStoredAuthSession();
          resetAuthState();
          openWalletAuthDialog();
          return;
        }
        closeWalletAuthDialog();
        setStoredAuthSession(createWalletAuthSession(walletAddress, signature));
        if (isMounted) {
          setAuthenticatedUser({
            userType: user.userType,
            username: user.username,
            email: user.email,
            walletAddress: user.walletAddress ?? walletAddress,
          });
        }
      } catch (error) {
        console.error("Failed to restore wallet session", error);
        clearStoredAuthSession();
        resetAuthState();
        openWalletAuthDialog();
      } finally {
        finishAuthCheck();
      }
    };

    const handleStoredEmailLogin = async () => {
      if (storedSession?.mode !== "email") {
        return;
      }

      try {
        const user = await signInWithEmail(storedSession.email, storedSession.password);
        if (!user || !isAllowedAdminUserType(user.userType)) {
          clearStoredAuthSession();
          resetAuthState();
          return;
        }

        if (isMounted) {
          setAuthenticatedUser({
            userType: user.userType,
            username: user.username,
            email: user.email ?? storedSession.email,
            walletAddress: user.walletAddress,
          });
        }
      } catch (error) {
        console.error("Failed to sign in with email", error);
        resetAuthState();
      } finally {
        finishAuthCheck();
      }
    };

    if (isConnecting || isReconnecting) {
      // Keep the current session state while wagmi is restoring a connection.
      return () => {
        isMounted = false;
      };
    }

    if (storedSession?.mode === "wallet") {
      if (!isConnected || !address) {
        clearStoredAuthSession();
        closeWalletAuthDialog();
        resetAuthState();
        finishAuthCheck();
      } else if (areEqual(storedSession.walletAddress, address)) {
        restoreWalletSession(address, storedSession.credential);
      } else {
        clearStoredAuthSession();
        resetAuthState();
        openWalletAuthDialog();
        finishAuthCheck();
      }
    } else if (storedSession?.mode === "email") {
      closeWalletAuthDialog();
      handleStoredEmailLogin();
    } else if (isConnected && address) {
      resetAuthState();
      openWalletAuthDialog();
      finishAuthCheck();
    } else {
      closeWalletAuthDialog();
      resetAuthState();
      finishAuthCheck();
    }

    return () => {
      isMounted = false;
    };
  }, [isConnected, isConnecting, isReconnecting, address]);

  const disconnectWalletConnection = () => {
    setAuthenticatedUser(null);
    setWalletAuthError(null);
    setIsWalletAuthSubmitting(false);
    setIsWalletAuthDialogOpen(false);
    disconnect();
  };

  const handleWalletDisconnect = () => {
    clearStoredAuthSession();
    disconnectWalletConnection();
  };

  const handleWalletAuthentication = async () => {
    if (!address) {
      setWalletAuthError("Connect a wallet before signing the message.");
      return;
    }

    setWalletAuthError(null);
    setIsWalletAuthSubmitting(true);

    try {
      const signature = await signMessageAsync({
        message: "Please sign this message to connect to Ninfa.",
      });
      const token = getEncryptedAuthToken(signature);
      const user = await signIn(address, token);

      if (!user) {
        throw new Error("Unable to authenticate this wallet.");
      }

      if (!isAllowedAdminUserType(user.userType)) {
        throw new Error("Only admins and printers can access this dashboard.");
      }

      setStoredAuthSession(createWalletAuthSession(address, signature));
      setAuthenticatedUser({
        userType: user.userType,
        username: user.username,
        email: user.email,
        walletAddress: user.walletAddress ?? address,
      });
      setIsWalletAuthDialogOpen(false);
    } catch (error) {
      console.error("Failed to authenticate wallet", error);
      setWalletAuthError(
        error instanceof Error
          ? error.message
          : "Wallet authentication failed. Try again or disconnect.",
      );
    } finally {
      setIsWalletAuthSubmitting(false);
    }
  };

  const handleEmailLogin = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedPassword = password.trim();
    const user = await signInWithEmail(normalizedEmail, normalizedPassword);

    if (!user) {
      throw new Error("Invalid email or password.");
    }

    if (!isAllowedAdminUserType(user.userType)) {
      throw new Error("Only admins and printers can access this dashboard.");
    }

    setStoredAuthSession(createEmailAuthSession(normalizedEmail, normalizedPassword));
    disconnectWalletConnection();
    setAuthenticatedUser({
      userType: user.userType,
      username: user.username,
      email: user.email ?? normalizedEmail,
      walletAddress: user.walletAddress,
    });
  };

  const storedSession = getStoredAuthSession();
  const userLabel = getAuthDisplayName(storedSession, authenticatedUser);
  const isLoggedIn = authenticatedUser !== null;
  const redirectPath = getDefaultAdminPath(authenticatedUser?.userType);
  const shouldRedirect =
    isLoggedIn &&
    !canAccessAdminPathname(authenticatedUser?.userType, pathname);

  return (
    <AuthenticatedUserContext.Provider value={authenticatedUser}>
    <div className={classes.maincontainer}>
      <Navbar
        isLoggedIn={isLoggedIn}
        userLabel={userLabel}
        userType={authenticatedUser?.userType}
        onLogout={handleWalletDisconnect}
      />
      {isAuthChecking ? (
        <Box className={classes.loadingScreen}>
          <CircularProgress size={28} sx={{ color: "#72ddf7" }} />
          <Typography className={classes.loadingLabel}>
            Restoring session...
          </Typography>
        </Box>
      ) : isLoggedIn ? (
        shouldRedirect ? (
          <Redirect to={redirectPath} />
        ) : (
          <Suspense fallback={<div>Loading...</div>}>
            <div className={classes.root}>
              <div className={classes.container}>{children}</div>
            </div>
          </Suspense>
        )
      ) : (
        <LoginPage onEmailLogin={handleEmailLogin} />
      )}
      <Dialog
        open={isWalletAuthDialogOpen}
        onClose={() => undefined}
        disableEscapeKeyDown
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Authenticate wallet</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 1.5, pt: 1 }}>
          <Typography color="text.secondary">
            Wallet connected as {address ? trimAddress(address) : "unknown"}.
            Sign a message to authenticate this admin session or disconnect the wallet.
          </Typography>
          {walletAuthError ? <Alert severity="error">{walletAuthError}</Alert> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleWalletDisconnect}
            color="inherit"
            disabled={isWalletAuthSubmitting}
          >
            Disconnect
          </Button>
          <Button
            onClick={handleWalletAuthentication}
            variant="contained"
            disabled={isWalletAuthSubmitting}
          >
            {isWalletAuthSubmitting ? "Waiting for signature..." : "Sign message"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    </AuthenticatedUserContext.Provider>
  );
};

const useStyles = makeStyles(() => ({
  maincontainer: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    // position: "relative",
    background: "#000",
    color: "#fff",
  },
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  loadingScreen: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    minHeight: "100dvh",
    gap: "0.9rem",
  },
  loadingLabel: {
    color: "rgba(214, 232, 255, 0.76)",
    fontSize: "0.95rem",
    letterSpacing: "0.02em",
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: "1920px",
    padding: "0 1.5rem",
    "@media (max-width:900px)": {
      padding: "0 1rem",
    },
    "@media (max-width:600px)": {
      padding: "0 0.5rem",
    },
  },
}));
export default Layout;
